import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface ActionItemsTableProps {
  companyId?: string;
  viewType: 'master' | 'personal';
  settings: any[];
  selectedTeamMember?: string;
}

const defaultRow = {
  mom_meeting_id: '',
  project_name: '',
  subproject: '',
  action_point: '',
  task_type: '',
  internal_owner: '',
  externalities: '',
  dependency: '',
  start_date: '',
  end_date: '',
  status: '',
  comments: ''
};

const ActionItemsTable: React.FC<ActionItemsTableProps> = ({ companyId, viewType, settings, selectedTeamMember }) => {
  const { userProfile } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Maintain an array of form rows instead of just one
  const [formRows, setFormRows] = useState<any[]>([{ ...defaultRow }]);
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchMeetings();
      fetchItems();
    }
  }, [companyId, viewType, selectedTeamMember]);

  const fetchMeetings = async () => {
    const { data } = await supabase.from('mom_meetings').select('id, meeting_title, meeting_date').eq('company_id', companyId);
    if (data) setMeetings(data);
  };

  const fetchItems = async () => {
    setLoading(true);
    let query = supabase
      .from('mom_action_items')
      .select('*, mom_meetings(meeting_date, meeting_title)')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });

    if (viewType === 'personal' && selectedTeamMember) {
      query = query.eq('internal_owner', selectedTeamMember);
    }

    const { data, error } = await query;
    if (!error && data) setItems(data);
    setLoading(false);
  };

  const updateFormRow = (index: number, field: string, value: string) => {
    const newRows = [...formRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setFormRows(newRows);
  };

  const sanitizeRowForDb = (row: any) => {
    const sanitized = { ...row };
    if (sanitized.start_date === '') sanitized.start_date = null;
    if (sanitized.end_date === '') sanitized.end_date = null;
    // Optional: map other empty strings to null to be clean
    if (sanitized.status === '') sanitized.status = null;
    return sanitized;
  };

  const handleSaveSingleItem = async (index: number) => {
    const row = formRows[index];
    if (!companyId || !row.mom_meeting_id || !row.action_point) {
      alert("Meeting and Action Point are required for the row being saved.");
      return;
    }

    const owner = viewType === 'personal' && selectedTeamMember ? selectedTeamMember : row.internal_owner;
    const cleanRow = sanitizeRowForDb(row);

    try {
      const { error } = await supabase
        .from('mom_action_items')
        .insert([{ ...cleanRow, internal_owner: owner, company_id: companyId }]);
      
      if (error) throw error;
      
      // Remove the saved row
      const newRows = [...formRows];
      newRows.splice(index, 1);
      if (newRows.length === 0) newRows.push({ ...defaultRow }); // always keep at least one
      setFormRows(newRows);
      
      fetchItems();
    } catch (err) {
      console.error('Error saving action item:', err);
      alert('Failed to save action item');
    }
  };

  const handleSaveAllValidRows = async () => {
    if (!companyId) return;
    setSavingAll(true);

    const validRows = formRows.filter(r => r.mom_meeting_id && r.action_point);
    const invalidRows = formRows.filter(r => !r.mom_meeting_id || !r.action_point);

    if (validRows.length === 0) {
      alert("No valid rows to save. Make sure Meeting and Action Point are filled.");
      setSavingAll(false);
      return;
    }

    try {
      const inserts = validRows.map(row => {
        const cleanRow = sanitizeRowForDb(row);
        return {
          ...cleanRow,
          internal_owner: viewType === 'personal' && selectedTeamMember ? selectedTeamMember : cleanRow.internal_owner,
          company_id: companyId
        };
      });

      const { error } = await supabase.from('mom_action_items').insert(inserts);
      if (error) throw error;

      // Keep only invalid rows (or reset to 1 empty row)
      setFormRows(invalidRows.length > 0 ? invalidRows : [{ ...defaultRow }]);
      fetchItems();
    } catch (err) {
      console.error('Error saving multiple items:', err);
      alert('Failed to save action items');
    } finally {
      setSavingAll(false);
    }
  };

  const calculateDaysLeft = (endDate: string) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    end.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffTime = end.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleUpdateExistingItemField = async (id: string, field: string, value: string) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    try {
      const { error } = await supabase.from('mom_action_items').update({ [field]: value }).eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      fetchItems();
    }
  };

  const getRowStyle = (item: any) => {
    const daysLeft = calculateDaysLeft(item.end_date);
    const statusLower = item.status?.toLowerCase() || '';
    const isCompleted = statusLower.includes('completed') || statusLower.includes('done') || statusLower.includes('closed') || statusLower.includes('resolved');

    if (isCompleted) {
      return { background: '#43a047', color: '#ffffff' }; // Dark Green
    }
    
    if (daysLeft === null) {
      return { background: '#ffffff', color: '#000000' };
    }

    if (daysLeft > 15) {
      return { background: '#a5d6a7', color: '#000000' }; // Light Green
    } else if (daysLeft >= 7) {
      return { background: '#ffe082', color: '#000000' }; // Yellow
    } else if (daysLeft >= 0) {
      return { background: '#ffcdd2', color: '#000000' }; // Light Red
    } else {
      return { background: '#d32f2f', color: '#ffffff' }; // Dark Red
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px',
    border: '1px solid transparent',
    background: 'transparent',
    fontSize: '0.875rem',
    outline: 'none',
    cursor: 'pointer',
    color: 'inherit'
  };

  const inputFocusStyle = { border: '1px solid #2563eb', background: '#fff', color: '#000' };

  const stickyStyle = (left: number, minWidth: number, zIndex: number = 1): React.CSSProperties => ({
    position: 'sticky',
    left: `${left}px`,
    minWidth: `${minWidth}px`,
    maxWidth: `${minWidth}px`,
    background: 'inherit',
    zIndex,
    boxShadow: '1px 0 0 #e2e8f0',
    padding: '12px 8px'
  });

  const normalColStyle: React.CSSProperties = {
    padding: '12px 8px',
    minWidth: '120px'
  };

  if (loading) return <div>Loading action items...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', alignItems: 'center' }}>
        <h2 style={{ fontSize: '1.25rem', margin: 0, fontWeight: 700 }}>
          {viewType === 'master' ? 'Master Dump (All Action Items)' : `${selectedTeamMember}'s Tracker`}
        </h2>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '8px', background: '#fff', maxHeight: '70vh', overflowY: 'auto', paddingBottom: '16px' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, textAlign: 'left', fontSize: '0.875rem', minWidth: '1600px' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
              <th style={{ ...stickyStyle(0, 50, 10), fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Sr. No.</th>
              <th style={{ ...stickyStyle(50, 120, 10), fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Meeting Date</th>
              <th style={{ ...stickyStyle(170, 120, 10), fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Project Name</th>
              <th style={{ ...stickyStyle(290, 120, 10), fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Subproject</th>
              <th style={{ ...stickyStyle(410, 250, 10), fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Action Point *</th>
              
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Task Type</th>
              {viewType === 'master' && <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Internal Owner</th>}
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Externalities</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Dependency</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Start Date</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>End Date</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0', minWidth: '80px' }}>Days Left</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Status</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Comments</th>
              <th style={{ ...normalColStyle, fontWeight: 600, borderBottom: '2px solid #e2e8f0', minWidth: '80px' }}>Action</th>
            </tr>
          </thead>
          <tbody>

            {/* Existing Items */}
            {items.map((item, index) => {
              const rowStyle = getRowStyle(item);
              const daysLeft = calculateDaysLeft(item.end_date);
              
              return (
                <tr key={item.id} style={{ ...rowStyle }}>
                  <td style={{ ...stickyStyle(0, 50), textAlign: 'center', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>{index + 1}</td>
                  <td style={{ ...stickyStyle(50, 120), borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {item.mom_meetings?.meeting_date ? new Date(item.mom_meetings.meeting_date).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ ...stickyStyle(170, 120), borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {item.project_name ? <span style={{ background: '#374151', color: 'white', padding: '4px 10px', borderRadius: '999px' }}>{item.project_name}</span> : '-'}
                  </td>
                  <td style={{ ...stickyStyle(290, 120), borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {item.subproject ? <span style={{ background: 'white', color: 'black', padding: '4px 10px', borderRadius: '999px', border: '1px solid #cbd5e1' }}>{item.subproject}</span> : '-'}
                  </td>
                  <td style={{ ...stickyStyle(410, 250), whiteSpace: 'normal', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>{item.action_point}</td>
                  
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {item.task_type ? <span style={{ color: '#7e22ce', fontWeight: 'bold', background: 'white', padding: '4px 10px', borderRadius: '999px', border: '1px solid #e9d5ff' }}>{item.task_type}</span> : '-'}
                  </td>
                  {viewType === 'master' && (
                    <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      {item.internal_owner ? <span style={{ background: '#374151', color: 'white', padding: '4px 10px', borderRadius: '999px' }}>{item.internal_owner}</span> : '-'}
                    </td>
                  )}
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {item.externalities ? <span style={{ background: 'white', color: 'black', padding: '4px 10px', borderRadius: '999px', border: '1px solid #cbd5e1' }}>{item.externalities}</span> : '-'}
                  </td>
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>{item.dependency || '-'}</td>
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>{item.start_date ? new Date(item.start_date).toLocaleDateString() : '-'}</td>
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>{item.end_date ? new Date(item.end_date).toLocaleDateString() : '-'}</td>
                  <td style={{ ...normalColStyle, fontWeight: 'bold', textAlign: 'center', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {daysLeft !== null ? daysLeft : '-'}
                  </td>
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                    {viewType === 'master' && !!item.internal_owner ? (
                      <span style={{ fontWeight: 'bold' }}>{item.status || '-'}</span>
                    ) : (
                      <select 
                        value={item.status || ''} 
                        onChange={e => handleUpdateExistingItemField(item.id, 'status', e.target.value)} 
                        style={{...inputStyle, padding: 0, fontWeight: 'bold', width: 'auto'}}
                      >
                        <option value="">-</option>
                        {settings.filter(s => s.category === 'Status').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={{ ...normalColStyle, whiteSpace: 'normal', borderBottom: '1px solid rgba(0,0,0,0.1)', padding: '0 4px', verticalAlign: 'top' }}>
                    {viewType === 'master' && !!item.internal_owner ? (
                      item.comments || '-'
                    ) : (
                      <textarea
                        defaultValue={item.comments || ''}
                        onBlur={e => {
                          if (e.target.value !== (item.comments || '')) {
                            handleUpdateExistingItemField(item.id, 'comments', e.target.value);
                          }
                        }}
                        style={{ ...inputStyle, resize: 'none', overflow: 'hidden', minHeight: '32px', lineHeight: '1.4', boxSizing: 'border-box' }}
                        rows={1}
                        placeholder="Add comment..."
                      />
                    )}
                  </td>
                  <td style={{ ...normalColStyle, borderBottom: '1px solid rgba(0,0,0,0.1)' }}></td>
                </tr>
              );
            })}
            {/* Inline Add Rows */}
            {viewType === 'master' && formRows.map((row, index) => (
              <tr 
                key={`form-row-${index}`} 
                style={{ background: '#f0fdf4' }}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node) && row.mom_meeting_id && row.action_point) {
                    handleSaveSingleItem(index);
                  }
                }}
              >
                <td style={{ ...stickyStyle(0, 50), textAlign: 'center', color: '#16a34a', fontWeight: 'bold', borderBottom: '2px solid #16a34a' }}>+</td>
                <td style={{ ...stickyStyle(50, 120), borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <select 
                    value={row.mom_meeting_id} 
                    onChange={e => updateFormRow(index, 'mom_meeting_id', e.target.value)} 
                    style={{ ...inputStyle, fontWeight: row.mom_meeting_id ? 'bold' : 'normal' }}
                    onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                    onBlur={e => Object.assign(e.target.style, { border: '1px solid transparent', background: 'transparent' })}
                  >
                    <option value="">Select...</option>
                    {meetings.map(m => <option key={m.id} value={m.id}>{new Date(m.meeting_date).toLocaleDateString()}</option>)}
                  </select>
                </td>
                <td style={{ ...stickyStyle(170, 120), borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <select 
                    value={row.project_name} 
                    onChange={e => updateFormRow(index, 'project_name', e.target.value)} 
                    style={{ ...inputStyle, background: row.project_name ? '#374151' : 'transparent', color: row.project_name ? 'white' : 'inherit', borderRadius: '999px', padding: '4px 8px' }}
                  >
                    <option value="">-</option>
                    {settings.filter(s => s.category === 'Projects').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                  </select>
                </td>
                <td style={{ ...stickyStyle(290, 120), borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <select 
                    value={row.subproject} 
                    onChange={e => updateFormRow(index, 'subproject', e.target.value)} 
                    style={{...inputStyle, background: row.subproject ? 'white' : 'transparent', color: row.subproject ? 'black' : 'inherit', borderRadius: '999px', padding: '4px 8px', border: row.subproject ? '1px solid #cbd5e1' : '1px solid transparent'}}
                  >
                    <option value="">-</option>
                    {settings.filter(s => s.category === 'Subprojects').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                  </select>
                </td>
                <td style={{ ...stickyStyle(410, 250), borderBottom: '2px solid #16a34a', padding: '0 4px', verticalAlign: 'top' }}>
                  <textarea 
                    placeholder="Type Action Point..." 
                    value={row.action_point} 
                    onChange={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                      updateFormRow(index, 'action_point', e.target.value);
                    }} 
                    style={{ ...inputStyle, resize: 'none', overflow: 'hidden', minHeight: '40px', lineHeight: '1.4', boxSizing: 'border-box' }} 
                    rows={1}
                    onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                    onBlur={e => Object.assign(e.target.style, { border: '1px solid transparent', background: 'transparent' })}
                  />
                </td>
                
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <select 
                    value={row.task_type} 
                    onChange={e => updateFormRow(index, 'task_type', e.target.value)} 
                    style={{ ...inputStyle, color: row.task_type ? '#7e22ce' : 'inherit', fontWeight: row.task_type ? 'bold' : 'normal', background: row.task_type ? 'white' : 'transparent', borderRadius: '999px', padding: '4px 8px', border: row.task_type ? '1px solid #e9d5ff' : '1px solid transparent' }}
                  >
                    <option value="">-</option>
                    {settings.filter(s => s.category === 'Task Type').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                  </select>
                </td>
                {viewType === 'master' && (
                  <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                    <select 
                      value={row.internal_owner} 
                      onChange={e => updateFormRow(index, 'internal_owner', e.target.value)} 
                      style={{ ...inputStyle, background: row.internal_owner ? '#374151' : 'transparent', color: row.internal_owner ? 'white' : 'inherit', borderRadius: '999px', padding: '4px 8px' }}
                    >
                      <option value="">-</option>
                      {settings.filter(s => s.category === 'Team').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                    </select>
                  </td>
                )}
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <select 
                    value={row.externalities} 
                    onChange={e => updateFormRow(index, 'externalities', e.target.value)} 
                    style={{...inputStyle, background: row.externalities ? 'white' : 'transparent', color: row.externalities ? 'black' : 'inherit', borderRadius: '999px', padding: '4px 8px', border: row.externalities ? '1px solid #cbd5e1' : '1px solid transparent'}}
                  >
                    <option value="">-</option>
                    {settings.filter(s => s.category === 'Externalities').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                  </select>
                </td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <input 
                    type="text" 
                    value={row.dependency} 
                    onChange={e => updateFormRow(index, 'dependency', e.target.value)} 
                    style={inputStyle} 
                    placeholder="-"
                    onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                    onBlur={e => Object.assign(e.target.style, { border: '1px solid transparent', background: 'transparent' })}
                  />
                </td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <input 
                    type="date" 
                    value={row.start_date} 
                    onChange={e => updateFormRow(index, 'start_date', e.target.value)} 
                    style={inputStyle} 
                  />
                </td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <input 
                    type="date" 
                    value={row.end_date} 
                    onChange={e => updateFormRow(index, 'end_date', e.target.value)} 
                    style={inputStyle} 
                  />
                </td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', textAlign: 'center', color: 'var(--muted)', padding: '0 4px' }}>-</td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px' }}>
                  <select 
                    value={row.status} 
                    onChange={e => updateFormRow(index, 'status', e.target.value)} 
                    style={inputStyle}
                  >
                    <option value="">-</option>
                    {settings.filter(s => s.category === 'Status').map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
                  </select>
                </td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '0 4px', verticalAlign: 'top' }}>
                  <textarea 
                    value={row.comments} 
                    onChange={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                      updateFormRow(index, 'comments', e.target.value);
                    }} 
                    style={{ ...inputStyle, resize: 'none', overflow: 'hidden', minHeight: '40px', lineHeight: '1.4', boxSizing: 'border-box' }} 
                    rows={1}
                    placeholder="-"
                    onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                    onBlur={e => Object.assign(e.target.style, { border: '1px solid transparent', background: 'transparent' })}
                  />
                </td>
                <td style={{ ...normalColStyle, borderBottom: '2px solid #16a34a', padding: '8px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {/* Auto-saves on blur of the row, Save button removed */}
                    {formRows.length > 1 && (
                      <button 
                        onClick={() => {
                          const newRows = [...formRows];
                          newRows.splice(index, 1);
                          setFormRows(newRows);
                        }} 
                        style={{ padding: '4px 8px', fontSize: '0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '9999px', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {items.length === 0 && (
              <tr>
                <td colSpan={viewType === 'master' ? 15 : 14} style={{ padding: 0, background: 'white' }}>
                  <div style={{ position: 'sticky', left: '50%', transform: 'translateX(-50%)', width: 'max-content', textAlign: 'center', padding: '4rem 0', color: 'var(--muted)' }}>
                    <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No action items found yet.</div>
                    {viewType === 'master' && <div>Add your first action item using the green row below!</div>}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewType === 'master' && (
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
          <button 
            onClick={() => setFormRows([...formRows, { ...defaultRow }])}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 500 }}
          >
            + Add 1 Row
          </button>
          <button 
            onClick={() => setFormRows([...formRows, ...Array(5).fill({ ...defaultRow })])}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 500 }}
          >
            + Add 5 Rows
          </button>
          
          {formRows.filter(r => r.mom_meeting_id && r.action_point).length > 1 && (
            <button 
              onClick={handleSaveAllValidRows}
              disabled={savingAll}
              style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#22c55e', color: 'white', cursor: 'pointer', fontWeight: 600, marginLeft: 'auto' }}
            >
              {savingAll ? 'Saving...' : `Save All Valid Rows (${formRows.filter(r => r.mom_meeting_id && r.action_point).length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionItemsTable;
